import * as React from 'react';

import { FormSelectField, SelectFieldProps } from 'ui';
import { PhoneCountryCodeOptions } from '@tock/shared/constants/PhoneCountryCodeOptions';
import { useFeatureFlagForConsumer } from 'featureFlags/useConsumerFeatureFlag';

export interface FormPhoneCountryCodeFieldProps extends Pick<
  SelectFieldProps,
  Exclude<keyof SelectFieldProps, 'children'>
> {
  phoneFieldName?: string;
  children?: React.ReactNode;
  /**
   * Disallow selection of sanctioned phone country codes. They will appear in the select but be disabled.
   * Whether this prop has any effect or not is also controlled by the
   * DISALLOW_FRONTEND_SANCTIONED_PHONE_COUNTRY_CODE_SELECTION feature flag; both the prop and the
   * flag are needed for a behavior change.
   */
  disallowSanctionedCodes?: boolean;
}

const FormPhoneCountryCodeField = ({
  name = 'phoneCountryCode',
  id = 'phoneCountryCode',
  phoneFieldName = 'phone',
  fullWidth,
  className,
  label = 'Phone country code',
  onChange,
  disallowSanctionedCodes = false,
  ...rest
}: FormPhoneCountryCodeFieldProps) => {
  const canDisallowSanctionedCodes = useFeatureFlagForConsumer(
    'DISALLOW_FRONTEND_SANCTIONED_PHONE_COUNTRY_CODE_SELECTION'
  );
  disallowSanctionedCodes = disallowSanctionedCodes && canDisallowSanctionedCodes;

  return (
    <FormSelectField
      fullWidth={fullWidth}
      name={name}
      id={id}
      label={label}
      onChange={onChange}
      SelectProps={{
        inputProps: {
          'data-testid': 'phone-country-code',
          autoComplete: 'tel-country-code',
        },
      }}
      {...rest}
    >
      <option value="" />
      <PhoneCountryCodeOptions disallowSanctionedCodes={disallowSanctionedCodes} />
    </FormSelectField>
  );
};

export default FormPhoneCountryCodeField;
